import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLayout } from '../../context/LayoutContext';
import LogoutButton from '../Auth/LogoutButton';
import {
  BellIcon,
  UserCircleIcon,
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  KeyIcon,
  ArrowRightOnRectangleIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { sidebarOpen, toggleSidebar, isMobile } = useLayout();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleQuickLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Erro no logout:', error);
      window.location.href = '/login';
    }
  };
  const [systemConfig, setSystemConfig] = useState<any>({
    company_logo: '',
    company_name: 'Lexpress Sistema'
  });

  useEffect(() => {
    // Carregar configuração do sistema
    const loadConfig = () => {
      try {
        const savedConfig = localStorage.getItem('system_config');
        if (savedConfig) {
          const config = JSON.parse(savedConfig);
          setSystemConfig(config);
        }
      } catch (err) {
        console.error('Erro ao carregar configuração:', err);
      }
    };

    loadConfig();

    // Escutar mudanças na configuração
    const handleConfigUpdate = (event: any) => {
      setSystemConfig(event.detail);
    };

    window.addEventListener('system-config-updated', handleConfigUpdate);
    return () => {
      window.removeEventListener('system-config-updated', handleConfigUpdate);
    };
  }, []);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (userMenuOpen && !target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [userMenuOpen]);

  return (
    <header className="bg-white shadow-sm border-b border-secondary-200 h-16 fixed w-full top-0 z-50">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Mobile menu button + Logo */}
        <div className="flex items-center space-x-2">
          {/* Mobile menu button */}
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg lg:hidden"
            >
              {sidebarOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          )}
          
          {/* Logo */}
          {systemConfig.company_logo && (
            <img
              src={systemConfig.company_logo}
              alt={systemConfig.company_name || 'Logo'}
              className="h-10 w-auto max-w-32 object-contain"
            />
          )}
          <h1 className="text-lg lg:text-xl font-bold text-primary-600 truncate">
            {systemConfig.company_name || 'Lexpress Sistema'}
          </h1>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <button className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg">
            <BellIcon className="h-4 w-4" />
          </button>

          {/* Quick Logout Button */}
          <button
            onClick={handleQuickLogout}
            className="hidden md:flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg border border-red-200 hover:border-red-300 transition-colors"
            title="Fazer logout"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1" />
            Sair
          </button>

          {/* User menu */}
          <div className="relative user-menu-container">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-secondary-100"
            >
              <UserCircleIcon className="h-8 w-8 text-secondary-500" />
              <div className="text-left">
                <div className="text-sm font-medium text-secondary-900">
                  {user?.first_name || user?.username}
                </div>
                <div className="text-xs text-secondary-500">
                  {user?.email}
                </div>
              </div>
              <ChevronDownIcon className="h-4 w-4 text-secondary-500" />
            </button>

            {/* Dropdown menu */}
            <div className={`absolute right-0 mt-1 w-48 bg-white border border-secondary-200 rounded-lg shadow-lg transition-all duration-200 z-50 ${
              userMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
            }`}>
              <div className="py-1">
                <Link
                  to="/profile"
                  className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100 rounded-t-md"
                >
                  <UserIcon className="h-4 w-4 mr-1.5" />
                  Meu Perfil
                </Link>
                <Link
                  to="/change-password"
                  className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100"
                >
                  <KeyIcon className="h-4 w-4 mr-1.5" />
                  Alterar Senha
                </Link>
                <hr className="border-secondary-200" />
                <LogoutButton />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;